<script setup lang="ts">
import { GroupOption, ItemOption } from "../MSelect";
import { nextTick, watch, computed, withDefaults, ref, onMounted, onUnmounted } from "vue";
import { getScrollParent, handleScrollHittingBottom } from "../../../../utils/misc";
import { useDebouncedThrottledFunction } from "../../../../composables/throttleDebounce";

const props = withDefaults(
  defineProps<{
    focusedItem: any;
    items: Array<GroupOption | ItemOption>;
    value: any;
    disabled?: boolean;
    virtualList?: boolean;
    itemsPerPage?: number;
    pageNumber?: number;
  }>(),
  { virtualList: true, itemsPerPage: 200 },
);

const pageNumber = ref<number>(1);
const pageSize = computed<number>(() => props.itemsPerPage * pageNumber.value - 1);
const content = ref<HTMLElement>();
const scrollParent = ref<HTMLElement>();

const { debouncedThrottled: debouncedThrottledHandleScroll } = useDebouncedThrottledFunction(
  (e: Event) => handleScrollHittingBottom(e, 150, loadNextPage),
  150,
  150,
);

onMounted(async () => {
  pageNumber.value = 1;
  scrollParent.value = getScrollParent(content.value) as HTMLElement;
  scrollParent.value.addEventListener("scroll", debouncedThrottledHandleScroll);
});

onUnmounted(() => {
  if (scrollParent.value) {
    scrollParent.value.removeEventListener("scroll", debouncedThrottledHandleScroll);
  }
});

const emit = defineEmits({
  select: (_options: ItemOption) => true,
});

function onSelect(option: ItemOption) {
  emit("select", option);
}

watch(
  () => props.disabled,
  (v) => {
    pageNumber.value = !v ? 1 : pageNumber.value;
  },
);

watch(
  () => props.focusedItem,
  (v) =>
    nextTick(() => {
      const selectedElement = document.getElementById(`select-item-${stringifyValues(v.value)}`);
      if (pageSize.value === +selectedElement?.getAttribute("data-index") + 1) {
        loadNextPage();
      }
      selectedElement?.scrollIntoView({ block: "end" });
    }),
);

const loadNextPage = () => {
  pageNumber.value++;
};

const itemsFlattened = computed(() =>
  props.items
    .map((subItems) => [
      { groupTitle: subItems?.title, groupSize: subItems?.items?.length },
      ...(subItems?.items ?? []),
    ])
    .flat(),
);

const getPagedItems = computed(() => {
  const flattenedItems = itemsFlattened.value;

  if (!props.virtualList) {
    return flattenedItems;
  }

  const startIndex = 0;
  return flattenedItems.slice(startIndex, pageSize.value);
});

function stringifyValues(value: any) {
  return JSON.stringify(value);
}
</script>
<template>
  <div ref="content" class="select-render-list">
    <template
      v-for="(item, k) in getPagedItems"
      :key="
        item.groupTitle
          ? `item-outer-${k}${item.groupTitle}${item.label}`
          : `item-inner-${item.label}-${k}`
      "
    >
      <div
        v-if="'groupTitle' in item"
        :class="['simple-select-item', 'title', disabled]"
        :data-index="k"
        role="heading"
      >
        <p class="m-select-item-label">{{ item.groupTitle }}</p>
      </div>
      <div
        v-else
        :id="`select-item-${stringifyValues(item.value)}`"
        :class="{
          'simple-select-item': true,
          selected: item.value === value,
          focused: item === focusedItem,
          title: 'title' in item,
          disabled,
        }"
        :data-index="k"
        @click="onSelect(item)"
      >
        <p class="m-select-item-label">{{ item.label }}</p>
      </div>
    </template>
  </div>
</template>
<style lang="scss">
@import "SelectRenderList.scss";
</style>
